import { Outlet, useMatches } from '@tanstack/react-router'

import { BrandLogo } from '@/components/common/brand-logo'
import { LanguageSelector } from '@/components/common/language-selector'
import { ThemeToggle } from '@/components/common/theme-toggle'
import { cn } from '@/lib/utils'
import { useWhiteLabelStore } from '@/stores/white-label'

import { LegalFooter } from './components/legal-footer'
import { PhotoPanel } from './components/photo-panel'

/**
 * Two class sets over one tree. Rendering two alternative trees would mount the
 * form — and the Turnstile widget inside it — twice, so the panel is a class
 * decision, never a second <Outlet />.
 */
const SHELL = {
  classic: {
    root: '',
    body: 'flex flex-1 flex-col items-center p-4 py-8 md:p-10',
    column: 'my-auto w-full max-w-sm space-y-6',
    header: 'flex items-center justify-between',
    logo: 'pl-2',
    controls: 'flex items-center gap-2',
    card: '',
    inlineFooter: '',
    panel: '',
    footer: '',
  },
  split: {
    // The photo panel and the naked form both sit on the card surface, so the
    // separator chip inside the form (bg-card) blends into the page instead of
    // reading as a pill over the muted tint.
    root: 'lg:bg-card',
    body: 'flex flex-1 flex-col items-center p-4 py-8 md:p-10 lg:grid lg:w-full lg:grid-cols-2 lg:items-stretch lg:gap-0 lg:p-0',
    // `lg:order-2` swaps only the VISUAL order: the column stays physically
    // first in the DOM so keyboard/tab order reaches the form before the
    // marketing panel. `min-h-dvh` gives the column the full viewport so the
    // card centres between the header row and the inline legal block.
    column:
      'my-auto w-full max-w-sm space-y-6 lg:order-2 lg:my-0 lg:flex lg:min-h-dvh lg:w-full lg:max-w-none lg:flex-col lg:space-y-0 lg:px-8 lg:py-6',
    // At lg the wordmark lives on the photo panel, so the header keeps only
    // the language/theme controls, right-aligned over the page background —
    // no colour overrides needed on a light surface.
    header: 'flex items-center justify-between lg:justify-end',
    logo: 'pl-2 lg:hidden',
    controls: 'flex items-center gap-2',
    // The reference look is a naked form: the Card is de-chromed at lg
    // (ring/shadow/bg/padding off) instead of restyled, so the pages keep
    // rendering their Card untouched and white-label never changes.
    // `overflow-visible` undoes the Card's own `overflow-hidden`: with the
    // chrome gone the inputs sit flush with the card edge, and the clip was
    // amputating their focus ring.
    card: 'lg:mx-auto lg:my-auto lg:w-full lg:max-w-md lg:[&_[data-slot=card]]:overflow-visible lg:[&_[data-slot=card]]:bg-transparent lg:[&_[data-slot=card]]:py-0 lg:[&_[data-slot=card]]:shadow-none lg:[&_[data-slot=card]]:ring-0 lg:[&_[data-slot=card-content]]:px-0 lg:[&_[data-slot=card-header]]:px-0',
    inlineFooter: 'hidden lg:mx-auto lg:mt-8 lg:block lg:w-full lg:max-w-md',
    // Sticky within the layout's scroll container: the photo stays put while
    // a tall form (register) scrolls beside it.
    panel: 'hidden lg:sticky lg:top-4 lg:m-4 lg:block lg:h-[calc(100dvh-2rem)]',
    footer: 'lg:hidden',
  },
} as const

export default function AuthLayout() {
  const { isWhiteLabel } = useWhiteLabelStore()
  const matches = useMatches()

  // Guarded by the RENDERED matches, not useLocation: during a navigation
  // the location already points at the target while the outlet still renders
  // the previous screen (lazy chunk + context invalidation), so a
  // pathname-based guard flashes the split layout around the still-mounted
  // switch screen the moment an account is picked. `state.matches` only
  // swaps when the new screen actually commits, in both directions.
  const isSwitchScreen = matches.some((match) => match.pathname === '/switch')
  const showPhotoPanel = !isWhiteLabel && !isSwitchScreen
  const s = showPhotoPanel ? SHELL.split : SHELL.classic

  return (
    <div className={cn('bg-muted/30 flex h-dvh flex-col', s.root)}>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className={s.body}>
          {/* Card column is physically first in the DOM (visual order swaps
              via `lg:order-2`) so keyboard/tab order reaches the form before
              the marketing panel. `PhotoPanel` is `hidden lg:block`, so its
              DOM position has no effect below `lg`. */}
          <div className={s.column}>
            <div className={s.header}>
              <BrandLogo size={32} className={s.logo} />
              <div className={s.controls}>
                <LanguageSelector iconClassName="h-5 w-5" />
                <ThemeToggle iconClassName="h-5 w-5" />
              </div>
            </div>

            <div className={s.card}>
              <Outlet />
            </div>

            {showPhotoPanel && (
              <LegalFooter variant="inline" className={s.inlineFooter} />
            )}
          </div>

          {showPhotoPanel && <PhotoPanel className={s.panel} />}
        </div>

        {!isSwitchScreen && <LegalFooter className={s.footer} />}
      </div>
    </div>
  )
}
